import React, {useEffect, useRef, useState} from 'react';
import styles from "./company.module.scss";
import {useTickerRating} from "../../../hooks/tickerRating.hook";
import {IRatingTicker, namesOfTypesOfRating, TypesOfRating} from "../../../shared/types";
import {Button, Input, message as messageAnt, Select, SelectProps, Checkbox, DatePicker, Switch} from "antd";
import type {CheckboxProps, DatePickerProps} from 'antd';
import {v4 as uuidv4} from 'uuid';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faXmark} from '@fortawesome/free-solid-svg-icons'
import {useAppSelector} from "../../../store/store";
import dayjs from "dayjs";
import type {Dayjs} from 'dayjs';
import {CountryExchange} from "../../../shared/countryExchange";
import {EstimateOfFinancialState, FinancialState} from "../../../shared/investingComConfig";
import {delay} from "../../../shared/lib/lib";

interface Props {
    country: CountryExchange
    tickerList: string[]
}

const Company = ({country, tickerList}: Props) => {

    const {loading: loadingTickerRating, saveTickerRating, saveAllTickerRatings, getAllTickerRatings, getAllTickerRatingsFromOutResource, removeTickerRating, removeAllTickerRatings, saveDateExpRating, error: errorRating, clearError: clearErrorRating} = useTickerRating()

    const [optionsSelectTicker, setOptionsSelectTicker] = useState<SelectProps['options']>()//массив тикеров, доступных для выбора
    const [tickerForAdding, setTickerForAdding] = useState<string>('')
    const [ratingForAdding, setRatingForAdding] = useState<string>('')
    const [typeOfRatingToSetDateExp, setTypeOfRatingToSetDateExp] = useState<TypesOfRating | undefined>()
    const [dateToSetDateExp, setDateToSetDateExp] = useState<dayjs.Dayjs | null>(null)
    const [ratingList, setRatingList] = useState<IRatingTicker[]>([])
    const [ratingListFromOut, setRatingListFromOut] = useState<IRatingTicker[]>([])//список рейтингов компаний, который собрали с сайта Investing.com, для сохранения в БД
    const [markedTickerInRatingList, setMarkedTickerInRatingList] = useState<string>()//тикер который выделен с таблице
    const [needUpdateRatingList, setNeedUpdateRatingList] = useState(true)
    const [saveTickerBtnDisabled, setSaveTickerBtnDisabled] = useState(true)//блокировка кнопки добавления в список рейтинга если не все необходимые данные выбраны
    const [setDateExpBtnDisabled, setSetDateExpBtnDisabled] = useState(true)//блокировка кнопки настройки даты экспирации рейтинга если не все необходимые данные выбраны
    const [btnRemoveRatingsIsLoading, setBtnRemoveRatingsIsLoading] = useState<boolean>(false);//состояние кнопки Удалить
    const [btnGetRatingsIsLoading, setBtnGetRatingsIsLoading] = useState<boolean>(false);//состояние кнопки Получить
    const [btnSaveRatingsIsLoading, setBtnSaveRatingsIsLoading] = useState<boolean>(false);//состояние кнопки Сохранить
    const [autoSaveTrigger, setAutoSaveTrigger] = useState<boolean>(false)//выключатель автосохранения порции рейтингов

    const btnGetRef = useRef<HTMLButtonElement>(null)
    const btnSaveRef = useRef<HTMLButtonElement>(null)

    //получение данных по рейтингам тикеров из БД
    const getAllRatings = async (country: CountryExchange) => {
        try {
            const data = await getAllTickerRatings(country)
            messageAnt.info(data.message)
            const ratings = data.info
            return ratings
        } catch (e) {
            messageAnt.info(errorRating);
            clearErrorRating()
            return undefined
        }
    }

    //изначальное получение данных по рейтингам тикеров из БД и при смене страны и маркера о необходимости обновления
    useEffect(() => {
        if (country && needUpdateRatingList) {
            const get = async () => {
                const allTickerRatings = await getAllRatings(country)
                if (allTickerRatings) {
                    //сохранение списка с рейтингами с предварительной сортировкой по алфавиту
                    allTickerRatings.sort((a, b) => a.ticker.localeCompare(b.ticker))
                    setRatingList(allTickerRatings)
                }
                setNeedUpdateRatingList(false)
            }
            get()
        }
    }, [country, needUpdateRatingList])

    //функция для сохранение рейтинга по тикеру в БД
    const saveRating = async (country: CountryExchange, ticker: string, fund: string) => {
        try {
            const data = await saveTickerRating(country, ticker, fund)
            messageAnt.info(data.message)
        } catch (e) {
            messageAnt.info(errorRating);
            clearErrorRating()
        }
    }

    //управление активностью кнопки добавления в список рейтинга
    useEffect(() => {
        if (tickerForAdding && ratingForAdding) {
            setSaveTickerBtnDisabled(false)
        } else {
            setSaveTickerBtnDisabled(true)
        }
    }, [tickerForAdding, ratingForAdding])

    //управление активностью кнопки установки даты экспирации рейтинга
    useEffect(() => {
        if (dateToSetDateExp) {
            setSetDateExpBtnDisabled(false)
        } else {
            setSetDateExpBtnDisabled(true)
        }
    }, [dateToSetDateExp])

    //формирование списков для селекторов выбора
    useEffect(() => {
        const optionsTicker: SelectProps['options'] = []
        tickerList.forEach((ticker) => {
            optionsTicker.push({value: ticker, label: ticker})
        })
        setOptionsSelectTicker(optionsTicker)
    }, [tickerList])

    //выбор инструмента для добавления в список рейтинга
    const onChangeTicker = (value: string) => {
        setTickerForAdding(value)
    };

    //ввод числового значения рейтинга для добавления в список рейтинга
    const onChangeRating = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const valueLastSymbol = value[value.length - 1]
        if (Number.parseInt(valueLastSymbol)) {
            setRatingForAdding(value)
        } else if (valueLastSymbol === '.') {
            setRatingForAdding(value)
        } else if (valueLastSymbol === ',') {
            const valueArr = value.split('')
            valueArr.splice(value.length - 1, 1, '.')
            const newValue = valueArr.join('')
            setRatingForAdding(newValue)
        } else if (value.length === 0) {
            setRatingForAdding('')
        }
    };

    //добавление тикера в список рейтингов
    const saveTickerHandler = async () => {
        if (country && tickerForAdding && ratingForAdding) {
            await saveRating(country, tickerForAdding, ratingForAdding)
            setNeedUpdateRatingList(true)
            setTickerForAdding('')
            setRatingForAdding('')
        }
    }

    //функция для удаления рейтинга по тикеру из БД
    const removeRating = async (country: CountryExchange, ticker: string) => {
        try {
            const data = await removeTickerRating(country, ticker)
            messageAnt.info(data.message)
        } catch (e) {
            messageAnt.info(errorRating);
            clearErrorRating()
        }
    }

    //удаление тикера из локального списка рейтингов
    const deleteTickerHandler = async (index: number) => {
        const areYouSure = window.confirm(`Вы уверены, что хотите удалить рейтинг по ${ratingList[index].ticker}?`)
        if (!areYouSure) {
            return
        }
        //что будем удалять
        const ticker = ratingList[index].ticker
        if (country && ticker) {
            await removeRating(country, ticker)
            setNeedUpdateRatingList(true)
        }
    }

    //выделение строки тикера в таблице
    const markTickerHandler = async (index: number) => {
        const markedTickerInRatingList = ratingList[index].ticker
        setMarkedTickerInRatingList(markedTickerInRatingList)
    }

    //выбор даты для установки даты экспирации
    const onChangeDateExp: DatePickerProps['onChange'] = (date, dateString) => {
        setDateToSetDateExp(date);
    };

    //функция для сохранение даты экспирации для рейтинга в БД
    const saveDateExpOfRating = async (country: CountryExchange, typeOfRating: TypesOfRating, dateExpirationOfRating: string) => {
        try {
            const data = await saveDateExpRating(country, typeOfRating, dateExpirationOfRating)
            messageAnt.info(data.message)
        } catch (e) {
            messageAnt.info(errorRating);
            clearErrorRating()
        }
    }

    //установка даты экспирации для списка рейтинга
    const saveDateExpHandler = async () => {
        if (country && dateToSetDateExp) {
            const dateString = dateToSetDateExp.format('YYYY-MM-DD')
            await saveDateExpOfRating(country, TypesOfRating.FUND, dateString)
            setDateToSetDateExp(null)
        }
    }

    //удаление всех рейтингов для получения новых
    const deleteRatingsHandler = async () => {
        const areYouSure = window.confirm(`Вы уверены, что хотите удалить все рейтинги?`)
        if (!areYouSure) {
            return
        }
        try {
            setBtnRemoveRatingsIsLoading(true)
            const data = await removeAllTickerRatings(country)
            messageAnt.info(data.message)
        } catch (e) {
            messageAnt.info(errorRating);
            clearErrorRating()
        } finally {
            setBtnRemoveRatingsIsLoading(false)
            setNeedUpdateRatingList(true)
        }
    }

    //получить новые рейтинги компаний с сайта investing.com
    const getRatingsHandler = async () => {
        if (country && tickerList) {
            try {
                setBtnGetRatingsIsLoading(true)
                const tickerListForGettingRating: string[] = []
                const tickerListAlreadyWithRating: string[] = []
                ratingList.forEach((elem) => {
                    tickerListAlreadyWithRating.push(elem.ticker)
                })
                for (let i = 0; i < tickerList.length; i++) {
                    if (!tickerListAlreadyWithRating.includes(tickerList[i])) {
                        tickerListForGettingRating.push(tickerList[i])
                        //получаю порционно по 50 компаний, нужно несколько нажатий кнопки Получить
                        if (tickerListForGettingRating.length === 50) {
                            break
                        }
                    }
                }
                if (tickerListForGettingRating.length === 0) {
                    messageAnt.info('Все рейтинги уже получены!')
                    messageAnt.info('Проверь, если есть "Не получен", замени на нужный или на "1"')
                    setBtnGetRatingsIsLoading(false)
                    setAutoSaveTrigger(false)
                    return
                }
                const data = await getAllTickerRatingsFromOutResource(country, tickerListForGettingRating)
                messageAnt.info(data.message)
                const ratings = data.info
                //перевод оценки с investing.com в виде "Отлично" в числовую оценку типа 4
                const ratingsNum: IRatingTicker[] = []
                ratings.forEach((elem) => {
                    let estimate = 'Не получен'
                    if (EstimateOfFinancialState[elem.rating as keyof typeof FinancialState]) {
                        estimate = EstimateOfFinancialState[elem.rating as keyof typeof FinancialState]
                    }
                    const newRat = {
                        ticker: elem.ticker,
                        rating: estimate
                    }
                    ratingsNum.push(newRat)
                })
                //локально сохраняем собранные рейтинги
                setRatingListFromOut([...ratingsNum])
            } catch (e) {
                messageAnt.info(errorRating);
                clearErrorRating()
            } finally {
                setBtnGetRatingsIsLoading(false)
            }
        }
    }

    //сохранить полученные рейтинги по компаниям в БД
    const saveRatingsHandler = async () => {
        if (country && ratingListFromOut && ratingListFromOut.length !== 0) {
            try {
                setBtnSaveRatingsIsLoading(true)
                //сохранить весь новый список фундаментальных рейтингов по компаниям
                const data = await saveAllTickerRatings(country, ratingListFromOut)
                messageAnt.info(data.message)
                setRatingListFromOut([])
            } catch (e) {
                messageAnt.info(errorRating);
                clearErrorRating()
            } finally {
                setNeedUpdateRatingList(true)
                setBtnSaveRatingsIsLoading(false)
            }
        }
    }

    //автосохранение, если включено и собралась порция рейтингов, то запуск сбора новой порции
    useEffect(() => {
        const func = async () => {
            if (autoSaveTrigger && ratingListFromOut.length !== 0) {
                //сохраняем собранную порцию рейтингов и там же обнуляется локальный массив с рейтингами
                await delay(10000)
                //сохраняем
                btnSaveRef.current?.click()
                await delay(10000)
                //запускаем сбор новой порции
                btnGetRef.current?.click()
            }
        }
        func()
    }, [ratingListFromOut])

    //включение/выключение автосохранения порции рейтингов
    const switchAutoSaveHandler = (checked: boolean) => {
        setAutoSaveTrigger(checked)
        if (checked) {
            messageAnt.info('Автосохранение включено')
        } else {
            messageAnt.info('Автосохранение выключено')
        }
    }

    //включение/выключение автосохранения порции рейтингов (клик по label)
    const switchAutoSaveHandlerLabelClick = () => {
        const state = autoSaveTrigger
        switchAutoSaveHandler(!state)
    }

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.wrap}>
                    <h5>Отбор фундаментально эффективных компаний</h5>
                    {
                        country &&
                        <div>
                            <div className={[styles.dateOfExpiration, styles.blockWrap].join(' ')}>
                                <p className={styles.title}>Установка даты экспирации рейтинга</p>
                                <DatePicker placeholder="Дата экспирации" onChange={onChangeDateExp}
                                            style={{width: 152}} size={"small"}
                                            value={dateToSetDateExp} popupStyle={{top: '140px', left: '23px'}}/>
                                <Button title={'Установить дату экспирации рейтинга'} type={'primary'}
                                        onClick={saveDateExpHandler}
                                        style={{width: 152}} size={"small"}
                                        disabled={setDateExpBtnDisabled}>Установить</Button>
                                <p className={styles.tipExpiration}>Обновлять рейтинг нужно в первых числах квартала</p>
                            </div>
                            <div className={[styles.addToRating, styles.blockWrap].join(' ')}>
                                <p className={styles.title}>Ручное добавление рейтинга компании</p>
                                <Select showSearch allowClear placeholder="Тикер" onChange={onChangeTicker}
                                        options={optionsSelectTicker} style={{width: 152}} size={"small"}
                                        value={tickerForAdding}/>
                                <Input placeholder="Фунд рейтинг" onChange={onChangeRating} style={{width: 152}}
                                       size={"small"}
                                       value={ratingForAdding}/>
                                <Button title={'Добавить тикер в рейтинг'} type={'primary'} onClick={saveTickerHandler}
                                        style={{width: 152}} size={"small"}
                                        disabled={saveTickerBtnDisabled}>Добавить</Button>
                            </div>
                            <div className={[styles.addToRating, styles.blockWrap].join(' ')}>
                                <p className={styles.title}>Автоматическое обновление рейтингов</p>
                                <div className={styles.switchOnAdvisors}>
                                    <Switch size="small" onChange={switchAutoSaveHandler}
                                            checked={autoSaveTrigger}/>
                                    <p onClick={switchAutoSaveHandlerLabelClick}>Автосохранение</p>
                                </div>
                                <Button title={'Удалить'} type={'primary'} onClick={deleteRatingsHandler}
                                        style={{width: 152}} size={"small"} disabled={ratingList.length === 0}
                                        loading={btnRemoveRatingsIsLoading}
                                >Удалить</Button>
                                <Button title={'Получить'} type={'primary'} onClick={getRatingsHandler}
                                        style={{width: 152}} size={"small"} disabled={ratingListFromOut.length !== 0}
                                        loading={btnGetRatingsIsLoading} ref={btnGetRef}
                                >Получить</Button>
                                <Button title={'Сохранить'} type={'primary'} onClick={saveRatingsHandler}
                                        style={{width: 152}} size={"small"} disabled={ratingListFromOut.length === 0}
                                        loading={btnSaveRatingsIsLoading} ref={btnSaveRef}
                                >Сохранить</Button>
                                <p className={styles.tip}>Рейтинги локально получены
                                    для {ratingListFromOut.length} компаний</p>
                                <p className={styles.tipTotal}>
                                    Всего получено рейтингов по {ratingList.length} компаний
                                    из {tickerList.length}
                                </p>
                            </div>
                            {
                                ratingList ?
                                    <div className={[styles.received, styles.blockWrap].join(' ')}>
                                        <p className={styles.title}>Список фундаментальных рейтингов компаний</p>
                                        <div className={styles.headTable}>
                                            <p className={styles.ticker}>Инструмент</p>
                                            <p className={styles.value}>Фунд рейтинг</p>
                                            <p className={styles.delete}></p>
                                        </div>
                                        <div className={styles.bodyTable}>
                                            {
                                                ratingList.length !== 0 &&
                                                ratingList.map((ratingElem, index) => (
                                                    <div
                                                        className={[styles.lineTable, markedTickerInRatingList === ratingElem.ticker ? styles.mark : ''].join(' ')}
                                                        key={uuidv4()}
                                                        onClick={() => markTickerHandler(index)}>
                                                        <p className={styles.ticker}>{ratingElem.ticker}</p>
                                                        <p className={styles.value}>{ratingElem.rating}</p>
                                                        <p className={styles.delete}>
                              <span onClick={() => deleteTickerHandler(index)}>
                                <FontAwesomeIcon icon={faXmark}/>
                              </span>
                                                        </p>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    </div>
                                    :
                                    null
                            }
                        </div>
                    }
                </div>
            </div>
        </div>
    );
};

export default Company;